import React, { useState, useEffect, useRef } from 'react';
import { Lock, Unlock, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuthStore } from './authStore';
import { cn } from '../../lib/utils';

interface UnlockModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UnlockModal({ open, onOpenChange }: UnlockModalProps) {
  const [passcode, setPasscode] = useState('');
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { unlock, loading, error, clearError } = useAuthStore();

  useEffect(() => {
    if (open) {
      setPasscode('');
      clearError();
      // Focus input after modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, clearError]);

  useEffect(() => {
    if (error) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passcode.trim()) return;

    const success = await unlock(passcode);
    if (success) {
      onOpenChange(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">管理员解锁</DialogTitle>
          <DialogDescription className="text-center">
            输入密码以解锁编辑功能
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div
            className={cn(
              "relative transition-transform",
              shake && "animate-[shake_0.5s_ease-in-out]"
            )}
          >
            <Input
              ref={inputRef}
              type="password"
              placeholder="请输入密码"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                "h-12 text-center text-lg tracking-widest",
                error && "border-destructive focus-visible:ring-destructive"
              )}
              disabled={loading}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              取消
            </Button>
            <Button type="submit" disabled={loading || !passcode.trim()}>
              {loading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  验证中...
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  解锁
                </>
              )}
            </Button>
          </DialogFooter>
        </form>

        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
